'use client';

import * as React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import { Skeleton } from '@workspace/ui/components/skeleton';
import { type WeddingSampleDto } from '~/data/budget-explorer/get-public-wedding-samples';

interface ItemDistributionChartProps {
  samples: WeddingSampleDto[];
  itemName: string;
  isLoading?: boolean;
  highlightValue?: number;
}

interface DistributionData {
  value: number;
  count: number;
  cumulative: number;
}

interface ItemStats {
  min: number;
  max: number;
  avg: number;
  median: number;
  q1: number;
  q3: number;
  count: number;
}

const BUCKET_COUNT = 20;

const COLORS = {
  primary: '#8b5cf6',
  primaryLight: '#a78bfa',
  highlight: '#10b981',
  grid: '#6b7280',
  text: '#9ca3af',
  border: '#374151',
  quartile: '#6b7280',
};

function calculateItemDistribution(
  samples: WeddingSampleDto[],
  itemName: string
): { data: DistributionData[]; stats: ItemStats } {
  const amounts: number[] = [];
  for (const sample of samples) {
    for (const item of sample.lineItems) {
      if (item.itemName.toLowerCase() === itemName.toLowerCase()) {
        amounts.push(item.amount);
      }
    }
  }

  if (amounts.length === 0) {
    return {
      data: [],
      stats: { min: 0, max: 0, avg: 0, median: 0, q1: 0, q3: 0, count: 0 },
    };
  }

  amounts.sort((a, b) => a - b);

  const min = amounts[0];
  const max = amounts[amounts.length - 1];
  const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
  const median = getMedian(amounts);
  const q1 = getPercentile(amounts, 25);
  const q3 = getPercentile(amounts, 75);

  const bucketSize = (max - min) / BUCKET_COUNT || 1;
  const data: DistributionData[] = [];
  let cumulative = 0;

  for (let i = 0; i < BUCKET_COUNT; i++) {
    const bucketMin = min + i * bucketSize;
    const bucketMax = bucketMin + bucketSize;
    const count = amounts.filter(
      (a) => a >= bucketMin && (i === BUCKET_COUNT - 1 ? a <= bucketMax : a < bucketMax)
    ).length;
    cumulative += count;

    data.push({
      value: Math.round((bucketMin + bucketMax) / 2),
      count,
      cumulative: Math.round((cumulative / amounts.length) * 100),
    });
  }

  return {
    data,
    stats: {
      min: Math.round(min),
      max: Math.round(max),
      avg: Math.round(avg),
      median: Math.round(median),
      q1: Math.round(q1),
      q3: Math.round(q3),
      count: amounts.length,
    },
  };
}

function getMedian(sortedArr: number[]): number {
  const mid = Math.floor(sortedArr.length / 2);
  return sortedArr.length % 2 !== 0
    ? sortedArr[mid]
    : (sortedArr[mid - 1] + sortedArr[mid]) / 2;
}

function getPercentile(sortedArr: number[], percentile: number): number {
  const index = (percentile / 100) * (sortedArr.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (upper >= sortedArr.length) return sortedArr[sortedArr.length - 1];
  if (lower < 0) return sortedArr[0];

  return sortedArr[lower] * (1 - weight) + sortedArr[upper] * weight;
}

export function ItemDistributionChart({
  samples,
  itemName,
  isLoading,
  highlightValue,
}: ItemDistributionChartProps): React.JSX.Element {
  const { data, stats } = React.useMemo(
    () => calculateItemDistribution(samples, itemName),
    [samples, itemName]
  );

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center text-muted-foreground">
        No data available for "{itemName}". Try selecting a different item.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-xs text-muted-foreground">Average</p>
          <p className="text-lg font-semibold">${stats.avg.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-xs text-muted-foreground">Median</p>
          <p className="text-lg font-semibold">${stats.median.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-xs text-muted-foreground">Range</p>
          <p className="text-lg font-semibold">
            ${stats.min.toLocaleString()} - ${stats.max.toLocaleString()}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-xs text-muted-foreground">Sample Size</p>
          <p className="text-lg font-semibold">{stats.count.toLocaleString()}</p>
        </div>
      </div>

      {/* Distribution Chart */}
      <div className="h-[350px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="itemAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.5} />
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.grid}33`} vertical={false} />
            <XAxis
              dataKey="value"
              tick={{ fontSize: 12, fill: COLORS.text }}
              tickLine={false}
              axisLine={{ stroke: COLORS.border }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <YAxis
              tick={{ fontSize: 12, fill: COLORS.text }}
              tickLine={false}
              axisLine={false}
              label={{
                value: 'Count',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 12, fill: COLORS.text },
              }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const distData = payload[0].payload as DistributionData;
                return (
                  <div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-lg">
                    <p className="font-medium">${distData.value.toLocaleString()}</p>
                    <p className="text-sm">
                      {distData.count} wedding{distData.count !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {distData.cumulative}% of weddings at or below
                    </p>
                  </div>
                );
              }}
            />
            <ReferenceLine
              x={stats.q1}
              stroke={COLORS.quartile}
              strokeDasharray="3 3"
              label={{
                value: 'Q1',
                position: 'top',
                fill: COLORS.quartile,
                fontSize: 10,
              }}
            />
            <ReferenceLine
              x={stats.median}
              stroke={COLORS.primary}
              strokeWidth={2}
              label={{
                value: 'Median',
                position: 'top',
                fill: COLORS.primary,
                fontSize: 10,
              }}
            />
            <ReferenceLine
              x={stats.q3}
              stroke={COLORS.quartile}
              strokeDasharray="3 3"
              label={{
                value: 'Q3',
                position: 'top',
                fill: COLORS.quartile,
                fontSize: 10,
              }}
            />
            {highlightValue && (
              <ReferenceLine
                x={highlightValue}
                stroke={COLORS.highlight}
                strokeWidth={2}
                label={{
                  value: 'Your Budget',
                  position: 'top',
                  fill: COLORS.highlight,
                  fontSize: 10,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="count"
              stroke={COLORS.primary}
              strokeWidth={2}
              fill="url(#itemAreaGradient)"
              animationDuration={800}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Quartile Legend */}
      <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5" style={{ backgroundColor: COLORS.quartile, borderStyle: 'dashed' }} />
          <span className="text-muted-foreground">
            25th percentile: ${stats.q1.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5" style={{ backgroundColor: COLORS.primary }} />
          <span>Median: ${stats.median.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5" style={{ backgroundColor: COLORS.quartile, borderStyle: 'dashed' }} />
          <span className="text-muted-foreground">
            75th percentile: ${stats.q3.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

